<script setup lang="ts">
  import CalendarPeoplePicker from '@/components/app/calendar/CalendarPeoplePicker.vue'
  import { calendarSupportsParticipantInvites } from '@/composables/useProviderCapabilities'
  import { useLawFieldTranslation } from '@/composables/useLawFieldTranslation'
  import { useLawTopicTranslation } from '@/composables/useLawTopicTranslation'
  import { useTranslation } from '@/composables/useTranslation'
  import type { CalendarGridEvent } from '@/types/calendar/grid'
  import type {
    CalendarEventRequest,
    CalendarEventTypeEnum,
    CustomerCalendar,
    CustomerCaseList,
    TUser
  } from '@/types/generated'
  import {
    CalendarEventTypeEnum as CalendarEventType,
    CasesService,
    PrivacyEnum as Privacy,
    UsersService
  } from '@/types/generated'
  import {
    externalParticipantEmails,
    normalizeExternalParticipantsForApi
  } from '@/types/calendar/participants'
  import { calendarDisplayLabel } from '@/utils/calendar/display'
  import { composerModeForEvent } from '@/utils/calendar/event-types'
  import { computed, onBeforeUnmount, ref, watch } from 'vue'

  const { translateLawField } = useLawFieldTranslation()
  const { translateLawTopic } = useLawTopicTranslation()

  type CalendarComposerFormState = Omit<CalendarEventRequest, 'participants_external'> & {
    mode: CalendarEventTypeEnum
    /** Email strings from CalendarPeoplePicker; normalised to API objects on save. */
    participantEmails: string[]
  }

  const props = withDefaults(
    defineProps<{
      open: boolean
      mode: CalendarEventTypeEnum
      event?: CalendarGridEvent | null
      calendars: CustomerCalendar[]
      defaultCalendarUuid?: string
      defaultOwnerUuid?: string | null
      presetDate?: string | null
      saving?: boolean
    }>(),
    {
      event: null,
      defaultCalendarUuid: '',
      defaultOwnerUuid: null,
      presetDate: null,
      saving: false
    }
  )

  const emit = defineEmits<{
    close: []
    save: [value: CalendarEventRequest]
  }>()

  const { t } = useTranslation({
    de: {
      termin: 'Termin',
      frist: 'Frist',
      wiedervorlage: 'Wiedervorlage',
      aufgabe: 'Aufgabe',
      gerichtstermin: 'Gerichtstermin',
      allgemeinerTermin: 'Allgemeiner Termin',
      privat: 'Privat',
      gerichtsterminHelp: 'Vorgeschlagene Orte aus dem Gerichtsverzeichnis',
      allgemeinerTerminHelp: 'Mandantengespräche, interne Termine',
      privatHelp: 'Persönlicher Eintrag, nicht freigegeben',
      save: 'Speichern',
      close: 'Schließen',
      addExternalEmail: 'Als externe E-Mail hinzufügen',
      notAvailableYet: 'Diese Variante folgt in einem späteren Schritt.',
      eventType: 'Art',
      title: 'Titel',
      titlePlaceholder: 'z. B. Mandantengespräch Schulz GmbH',
      optional: 'optional',
      fileReference: 'Aktenzeichen',
      fileReferencePlaceholder: 'Akte suchen ...',
      wegen: 'Wegen',
      wegenPlaceholder: 'Sachverhalt / Gegenstand',
      keywords: 'Schlagworte',
      keywordPlaceholder: 'hinzufügen ...',
      calendar: 'Kalender',
      owner: 'Sachbearbeiter:in',
      assistants: 'Assistenzen',
      participants: 'Teilnehmer:innen',
      participantsPlaceholder: 'Name oder E-Mail ...',
      userPlaceholder: 'Name suchen ...',
      start: 'Start',
      end: 'Ende',
      allDay: 'Ganztägig',
      location: 'Ort / Adresse',
      courtLocationPlaceholder: 'Gericht aus Verzeichnis wählen ...',
      defaultLocationPlaceholder: 'Konferenzraum, Adresse oder Link ...',
      notes: 'Notizen',
      notesPlaceholder: 'Details, Tagesordnung, Vorbereitungshinweise ...',
      noResults: 'Keine Treffer',
      linkedCase: 'Verknüpfte Akte',
      required: 'Pflichtfeld',
      startInPast: 'Der Termin darf nicht in der Vergangenheit beginnen.',
      saveChanges: 'Änderungen speichern',
      participantsInviteNote:
        'Dieser Kalenderanbieter unterstützt keine Einladungen. Teilnehmer:innen werden nur als Text gespeichert.',
      participantsPrivatNote: 'Bei privaten Terminen sind keine Teilnehmer:innen möglich.'
    },
    en: {
      termin: 'Appointment',
      frist: 'Deadline',
      wiedervorlage: 'Reschedule',
      aufgabe: 'Task',
      gerichtstermin: 'Court hearing',
      allgemeinerTermin: 'General appointment',
      privat: 'Private',
      gerichtsterminHelp: 'Suggested locations from the court directory',
      allgemeinerTerminHelp: 'Client meetings, internal appointments',
      privatHelp: 'Personal entry, not shared',
      save: 'Save',
      close: 'Close',
      addExternalEmail: 'Add as external email',
      notAvailableYet: 'This variant will follow in a later step.',
      eventType: 'Type',
      title: 'Title',
      titlePlaceholder: 'e.g. Client meeting Smith Corp',
      optional: 'optional',
      fileReference: 'File reference',
      fileReferencePlaceholder: 'Search case ...',
      wegen: 'Subject',
      wegenPlaceholder: 'Matter / subject',
      keywords: 'Keywords',
      keywordPlaceholder: 'add ...',
      calendar: 'Calendar',
      owner: 'Case owner',
      assistants: 'Assistants',
      participants: 'Participants',
      participantsPlaceholder: 'Name or email ...',
      userPlaceholder: 'Search name ...',
      start: 'Start',
      end: 'End',
      allDay: 'All day',
      location: 'Location / address',
      courtLocationPlaceholder: 'Choose court directory entry ...',
      defaultLocationPlaceholder: 'Room, address or link ...',
      notes: 'Notes',
      notesPlaceholder: 'Details, agenda, preparation notes ...',
      noResults: 'No results',
      linkedCase: 'Linked case',
      required: 'Required field',
      startInPast: 'The event cannot start in the past.',
      saveChanges: 'Save changes',
      participantsInviteNote:
        'This calendar provider does not support invites — participants are saved as text only.',
      participantsPrivatNote: 'Private events cannot have participants.'
    }
  })

  const pad = (value: number): string => value.toString().padStart(2, '0')
  const formatLocal = (date: Date): string =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
  const dateOnly = (value: string): string => value.slice(0, 10)
  const timeOnly = (value: string): string => value.slice(11, 16)
  const composeDateTime = (date: string, time: string): string => `${date}T${time}`
  const toApiDateTime = (value: string): string => new Date(value).toISOString()

  const roundNowOnDate = (isoDate?: string | null): string => {
    const date = new Date()
    date.setSeconds(0, 0)
    date.setMinutes(0)
    date.setHours(date.getHours() + 1)

    if (isoDate) {
      const [year, month, day] = isoDate.split('-').map(Number)
      date.setFullYear(year ?? date.getFullYear(), (month ?? 1) - 1, day ?? 1)
    }

    return formatLocal(date)
  }

  const addHour = (value: string): string => {
    const date = new Date(value)
    date.setHours(date.getHours() + 1)
    return formatLocal(date)
  }

  const editableCalendars = computed(() =>
    props.calendars.filter((calendar) => calendar.uuid && !calendar.calendar_readonly)
  )

  const resolvedDefaultCalendarUuid = computed(() => {
    if (props.defaultCalendarUuid) {
      return props.defaultCalendarUuid
    }

    const primaryCalendar = editableCalendars.value.find((calendar) => calendar.calendar_primary)
    if (primaryCalendar?.uuid) {
      return primaryCalendar.uuid
    }

    return editableCalendars.value[0]?.uuid ?? ''
  })

  const isEditing = computed(() => Boolean(props.event?.uuid))
  const saveLabel = computed(() => (isEditing.value ? t('saveChanges') : t('save')))

  const buildDefaultForm = (mode: CalendarEventTypeEnum): CalendarComposerFormState => {
    const start = roundNowOnDate(props.presetDate)
    const type =
      mode === CalendarEventType.FRIST ||
      mode === CalendarEventType.WIEDERVORLAGE ||
      mode === CalendarEventType.AUFGABE
        ? mode
        : CalendarEventType.TERMIN

    return {
      mode,
      calendar: resolvedDefaultCalendarUuid.value,
      type,
      title: '',
      start: toApiDateTime(start),
      end: toApiDateTime(addHour(start)),
      all_day: false,
      location_text: '',
      description: '',
      wegen: '',
      keywords: [],
      case: null,
      owner: props.defaultOwnerUuid ?? '',
      assistants: [],
      participants_internal: [],
      participantEmails: [],
      privacy: Privacy.NORMAL
    }
  }

  const buildFormFromEvent = (event: CalendarGridEvent): CalendarComposerFormState => ({
    mode: composerModeForEvent(event),
    calendar: event.calendarId,
    type: event.type,
    title: event.title,
    start: event.start,
    end: event.end,
    all_day: event.allDay,
    location_text: event.locationText ?? '',
    description: event.description ?? '',
    wegen: event.wegen ?? '',
    keywords: [...event.keywords],
    case: event.caseUuid ?? null,
    owner: event.ownerUuid,
    assistants: [...event.assistantUuids],
    participants_internal: [...event.participantInternalUuids],
    participantEmails: externalParticipantEmails(
      event.participantsExternalDetails?.length
        ? event.participantsExternalDetails
        : event.participantsExternal
    ),
    privacy: event.privacy ?? Privacy.NORMAL
  })

  const form = ref<CalendarComposerFormState>(buildDefaultForm(props.mode))
  const keywordInput = ref('')
  const caseQuery = ref('')
  const caseResults = ref<CustomerCaseList[]>([])
  const showCaseSuggestions = ref(false)
  const isSearchingCases = ref(false)
  const knownUsersByUuid = ref<Record<string, TUser>>({})
  const isLoadingUsers = ref(false)
  const users = computed(() => Object.values(knownUsersByUuid.value))

  let caseSearchTimer: number | null = null
  let caseSearchRequestId = 0
  let userSearchTimer: number | null = null

  onBeforeUnmount(() => {
    if (caseSearchTimer !== null) {
      window.clearTimeout(caseSearchTimer)
      caseSearchTimer = null
    }
    if (userSearchTimer !== null) {
      window.clearTimeout(userSearchTimer)
      userSearchTimer = null
    }
  })

  const modeTabs = computed(() => [
    { id: CalendarEventType.TERMIN, label: t('termin'), disabled: false },
    { id: CalendarEventType.FRIST, label: t('frist'), disabled: true },
    { id: CalendarEventType.WIEDERVORLAGE, label: t('wiedervorlage'), disabled: true },
    { id: CalendarEventType.AUFGABE, label: t('aufgabe'), disabled: true }
  ])

  const selectedCase = computed(
    () => caseResults.value.find((caseItem) => caseItem.uuid === form.value.case) ?? null
  )

  const caseDisplayLabel = (caseItem: CustomerCaseList): string => {
    const caseNumber = caseItem.jupus_case_number?.trim()
    const caseTitle = caseItem.case_title?.trim()

    if (caseNumber && caseTitle) {
      return `${caseNumber} — ${caseTitle}`
    }

    return caseNumber || caseTitle || ''
  }

  const eventCaseDisplayLabel = (event: CalendarGridEvent | null | undefined): string => {
    if (!event) {
      return ''
    }

    if (event.caseNumber && event.caseTitle) {
      return `${event.caseNumber} — ${event.caseTitle}`
    }

    return event.caseNumber || event.caseTitle || ''
  }

  const selectedCaseLabel = computed(() => {
    if (selectedCase.value) {
      return caseDisplayLabel(selectedCase.value)
    }

    if (form.value.case && props.event?.caseUuid === form.value.case) {
      return eventCaseDisplayLabel(props.event)
    }

    return ''
  })

  const caseSuggestionMeta = (caseItem: CustomerCaseList): string => {
    const lawField = translateLawField(caseItem.data?.law?.field)
    const lawTopic = translateLawTopic(caseItem.data?.law?.topic)

    return [lawField, lawTopic].filter(Boolean).join(' / ')
  }

  const localStart = computed<string>({
    get: () => formatLocal(new Date(form.value.start)),
    set: (value) => {
      form.value.start = toApiDateTime(value)
    }
  })

  const localEnd = computed<string>({
    get: () => formatLocal(new Date(form.value.end)),
    set: (value) => {
      form.value.end = toApiDateTime(value)
    }
  })

  const minimumStartLocal = computed(() => {
    const value = new Date()
    value.setSeconds(0, 0)
    return formatLocal(value)
  })
  const minimumStartDate = computed(() => dateOnly(minimumStartLocal.value))
  const minimumStartTime = computed(() =>
    dateOnly(localStart.value) === minimumStartDate.value
      ? timeOnly(minimumStartLocal.value)
      : undefined
  )
  const minimumEndDate = computed(() => dateOnly(localStart.value))
  const minimumEndTime = computed(() =>
    dateOnly(localEnd.value) === dateOnly(localStart.value) ? timeOnly(localStart.value) : undefined
  )

  const isTerminMode = computed(() => form.value.mode === CalendarEventType.TERMIN)
  const titleInvalid = computed(() => form.value.title.trim().length === 0)
  const calendarInvalid = computed(() => form.value.calendar.length === 0)
  const startInPast = computed(() => {
    if (!form.value.start) {
      return false
    }

    if (isEditing.value) {
      return false
    }

    if (form.value.all_day) {
      return dateOnly(localStart.value) < minimumStartDate.value
    }

    return new Date(form.value.start).getTime() < new Date().setSeconds(0, 0)
  })
  const startInvalid = computed(() => !form.value.start || startInPast.value)
  const endInvalid = computed(() => !form.value.end || form.value.end < form.value.start)
  const validationMessage = computed(() => {
    if (startInPast.value) {
      return t('startInPast')
    }

    return `${t('required')}: ${t('title')}, ${t('start')}, ${t('end')}, ${t('calendar')}`
  })

  const canSave = computed(() => {
    if (!isTerminMode.value) {
      return false
    }

    return !titleInvalid.value && !calendarInvalid.value && !startInvalid.value && !endInvalid.value
  })

  const currentTerminType = computed<CalendarEventTypeEnum>(() => {
    if (form.value.type === CalendarEventType.GERICHTSTERMIN) {
      return CalendarEventType.GERICHTSTERMIN
    }

    if (form.value.type === CalendarEventType.PRIVAT || form.value.privacy === Privacy.PRIVAT) {
      return CalendarEventType.PRIVAT
    }

    return CalendarEventType.TERMIN
  })

  const selectedCalendar = computed(() =>
    props.calendars.find((calendar) => calendar.uuid === form.value.calendar)
  )

  const participantInvitesSupported = computed(() =>
    calendarSupportsParticipantInvites(selectedCalendar.value)
  )

  const showParticipantsInviteNote = computed(
    () => !participantInvitesSupported.value && currentTerminType.value !== CalendarEventType.PRIVAT
  )

  const participantsDisabled = computed(() => currentTerminType.value === CalendarEventType.PRIVAT)

  const mergeUsers = (nextUsers: TUser[]): void => {
    knownUsersByUuid.value = {
      ...knownUsersByUuid.value,
      ...Object.fromEntries(nextUsers.map((user) => [user.uuid, user]))
    }
  }

  const selectedPeopleUuids = (): string[] => [
    ...new Set(
      [
        form.value.owner,
        ...(form.value.assistants ?? []),
        ...(form.value.participants_internal ?? [])
      ].filter(Boolean)
    )
  ]

  const loadUsers = async (query = ''): Promise<void> => {
    const trimmedQuery = query.trim()
    isLoadingUsers.value = true

    try {
      const response = await UsersService.usersList({
        isActive: true,
        ...(trimmedQuery ? { search: trimmedQuery } : {}),
        limit: 20
      })
      mergeUsers(response.results ?? [])
    } catch (error) {
      console.error('[calendar] loadUsers failed', error)
    } finally {
      isLoadingUsers.value = false
    }
  }

  const loadUsersByUuid = async (uuids: string[]): Promise<void> => {
    const missingUuids = uuids.filter((uuid) => uuid && !knownUsersByUuid.value[uuid])
    if (missingUuids.length === 0) {
      return
    }

    try {
      const users = await Promise.all(
        missingUuids.map(async (uuid) => {
          try {
            return await UsersService.usersRetrieve({ uuid })
          } catch {
            return null
          }
        })
      )
      mergeUsers(users.filter((user): user is TUser => Boolean(user)))
    } catch (error) {
      console.error('[calendar] loadUsersByUuid failed', error)
    }
  }

  const resetForm = (mode: CalendarEventTypeEnum): void => {
    form.value = props.event ? buildFormFromEvent(props.event) : buildDefaultForm(mode)
    keywordInput.value = ''
    caseQuery.value = props.event ? eventCaseDisplayLabel(props.event) : ''
    caseResults.value = []
    showCaseSuggestions.value = false
  }

  watch(
    () => props.open,
    (open) => {
      if (!open) {
        return
      }

      resetForm(props.mode)

      void loadUsers()
      void loadUsersByUuid(selectedPeopleUuids())
    },
    { immediate: true }
  )

  watch(
    () => props.event?.uuid ?? null,
    (eventUuid, previousEventUuid) => {
      if (props.open && eventUuid !== previousEventUuid) {
        resetForm(props.mode)
        void loadUsersByUuid(selectedPeopleUuids())
      }
    }
  )

  watch(
    () => props.defaultOwnerUuid,
    (ownerUuid) => {
      if (props.open && !form.value.owner && ownerUuid) {
        form.value.owner = ownerUuid
      }
    }
  )

  watch(
    () => selectedPeopleUuids().join(','),
    () => {
      if (props.open) {
        void loadUsersByUuid(selectedPeopleUuids())
      }
    }
  )

  watch(
    () => form.value.all_day,
    (allDay) => {
      if (!allDay) {
        return
      }

      localStart.value = `${dateOnly(localStart.value)}T00:00`
      localEnd.value = `${dateOnly(localEnd.value)}T23:59`
    }
  )

  watch(
    () => form.value.start,
    (start, previousStart) => {
      if (!start || !previousStart) {
        return
      }

      if (form.value.end < start) {
        localEnd.value = addHour(localStart.value)
        return
      }

      const currentStartDate = dateOnly(localStart.value)
      const previousStartDate = dateOnly(formatLocal(new Date(previousStart)))
      const currentEndDate = dateOnly(localEnd.value)

      if (currentStartDate !== previousStartDate && currentEndDate === previousStartDate) {
        localEnd.value = composeDateTime(currentStartDate, timeOnly(localEnd.value))
      }
    }
  )

  const searchCases = async (query: string): Promise<void> => {
    const trimmedQuery = query.trim()

    const requestId = ++caseSearchRequestId
    isSearchingCases.value = true

    try {
      const response = await CasesService.casesList({
        ...(trimmedQuery ? { search: trimmedQuery } : {}),
        limit: 8
      })

      if (requestId !== caseSearchRequestId) {
        return
      }

      caseResults.value = (response.results ?? []) as CustomerCaseList[]
    } catch {
      if (requestId === caseSearchRequestId) {
        caseResults.value = []
      }
    } finally {
      if (requestId === caseSearchRequestId) {
        isSearchingCases.value = false
      }
    }
  }

  watch(caseQuery, (query) => {
    if (query && query === selectedCaseLabel.value) {
      return
    }

    form.value.case = null

    if (caseSearchTimer) {
      window.clearTimeout(caseSearchTimer)
    }

    caseSearchTimer = window.setTimeout(() => {
      void searchCases(query)
    }, 200)
  })

  const pickCase = (caseItem: CustomerCaseList): void => {
    form.value.case = caseItem.uuid ?? null
    caseQuery.value = caseDisplayLabel(caseItem)
    showCaseSuggestions.value = false

    if (!form.value.owner && caseItem.clerk?.uuid) {
      form.value.owner = caseItem.clerk.uuid
    }
  }

  const clearCase = (): void => {
    form.value.case = null
    caseQuery.value = ''
    caseResults.value = []
  }

  const closeCaseSuggestions = (): void => {
    window.setTimeout(() => {
      showCaseSuggestions.value = false
    }, 120)
  }

  const onCaseFocus = (): void => {
    showCaseSuggestions.value = true

    if (caseSearchTimer) {
      window.clearTimeout(caseSearchTimer)
    }

    caseSearchTimer = window.setTimeout(() => {
      void searchCases(caseQuery.value)
    }, 0)
  }

  const addKeyword = (): void => {
    const value = keywordInput.value.trim()
    if (!value || (form.value.keywords ?? []).includes(value)) {
      keywordInput.value = ''
      return
    }

    form.value.keywords = [...(form.value.keywords ?? []), value]
    keywordInput.value = ''
  }

  const removeKeyword = (value: string): void => {
    form.value.keywords = (form.value.keywords ?? []).filter((keyword) => keyword !== value)
  }

  const onKeywordKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Enter' && event.key !== ',') {
      return
    }

    event.preventDefault()
    addKeyword()
  }

  const setMode = (mode: CalendarEventTypeEnum): void => {
    const tab = modeTabs.value.find((item) => item.id === mode)
    if (tab?.disabled || mode === form.value.mode) {
      return
    }

    form.value.mode = mode
    form.value.type =
      mode === CalendarEventType.TERMIN
        ? currentTerminType.value === CalendarEventType.GERICHTSTERMIN ||
          currentTerminType.value === CalendarEventType.PRIVAT
          ? currentTerminType.value
          : CalendarEventType.TERMIN
        : mode
    form.value.privacy =
      form.value.type === CalendarEventType.PRIVAT ? Privacy.PRIVAT : Privacy.NORMAL
  }

  const setTerminType = (type: CalendarEventTypeEnum): void => {
    form.value.type = type
    form.value.privacy = type === CalendarEventType.PRIVAT ? Privacy.PRIVAT : Privacy.NORMAL
    if (type === CalendarEventType.PRIVAT) {
      form.value.participants_internal = []
      form.value.participantEmails = []
    }
  }

  watch(participantsDisabled, (disabled) => {
    if (!disabled) {
      return
    }

    form.value.participants_internal = []
    form.value.participantEmails = []
  })

  const onPeopleQuery = (query: string): void => {
    if (userSearchTimer) {
      window.clearTimeout(userSearchTimer)
    }

    userSearchTimer = window.setTimeout(() => {
      void loadUsers(query)
    }, 200)
  }

  const submit = (): void => {
    if (!canSave.value || props.saving) {
      return
    }

    emit('save', {
      calendar: form.value.calendar,
      type: form.value.type,
      title: form.value.title.trim(),
      start: form.value.start,
      end: form.value.end,
      all_day: form.value.all_day,
      location_text: form.value.location_text?.trim() || undefined,
      description: form.value.description?.trim() || undefined,
      wegen: form.value.wegen?.trim() || undefined,
      keywords: (form.value.keywords ?? []).filter((keyword) => keyword.trim().length > 0),
      case: form.value.case ?? null,
      owner: form.value.owner || props.defaultOwnerUuid || '',
      assistants: [...(form.value.assistants ?? [])],
      participants_internal: [...(form.value.participants_internal ?? [])],
      participants_external: normalizeExternalParticipantsForApi(
        form.value.participantEmails ?? []
      ),
      privacy: form.value.type === CalendarEventType.PRIVAT ? Privacy.PRIVAT : Privacy.NORMAL
    })
  }
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-[80] flex items-center justify-center bg-black/35 p-4 backdrop-blur-[1px]"
      @click.self="emit('close')"
    >
      <div
        class="calendar-main border-card-line relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden
          rounded-xl border bg-background shadow-2xl"
      >
        <button
          type="button"
          class="text-muted-foreground-1 hover:bg-muted-hover focus:outline-hidden absolute end-2.5 top-2.5 z-10
            inline-flex size-7 items-center justify-center rounded-md"
          :aria-label="t('close')"
          @click="emit('close')"
        >
          <svg
            class="size-4 shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>

        <div class="flex justify-center px-5 pb-2 pt-4">
          <div class="bg-surface inline-flex rounded-full p-0.5">
            <button
              v-for="item in modeTabs"
              :key="item.id"
              type="button"
              :disabled="item.disabled"
              :aria-disabled="item.disabled"
              :class="[
                'focus:outline-hidden rounded-full px-3.5 py-1 text-xs transition-colors',
                form.mode === item.id
                  ? 'bg-primary font-medium text-primary-foreground shadow-sm'
                  : item.disabled
                    ? 'text-muted-foreground-1/60 cursor-not-allowed'
                    : 'text-muted-foreground-1 hover:text-foreground'
              ]"
              @click="setMode(item.id)"
            >
              {{ item.label }}
            </button>
          </div>
        </div>

        <div
          v-if="!isTerminMode"
          class="text-muted-foreground-1 grow overflow-y-auto px-5 py-10 text-center text-sm"
        >
          {{ t('notAvailableYet') }}
        </div>

        <div
          v-else
          class="grow overflow-y-auto bg-background px-5 py-5"
        >
          <div class="grid grid-cols-1 gap-5 lg:grid-cols-12">
            <section class="flex flex-col gap-y-4 lg:col-span-8">
              <div>
                <label class="text-muted-foreground-2 mb-1.5 block text-xs font-medium">
                  {{ t('eventType') }}
                </label>
                <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <button
                    type="button"
                    :class="[
                      'focus:outline-hidden rounded-lg border p-2.5 text-start text-xs',
                      currentTerminType === CalendarEventType.GERICHTSTERMIN
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-card-line hover:bg-card-hover bg-card text-foreground'
                    ]"
                    @click="setTerminType(CalendarEventType.GERICHTSTERMIN)"
                  >
                    <span class="block font-medium">{{ t('gerichtstermin') }}</span>
                    <span class="text-muted-foreground-1 mt-0.5 block text-[11px]">
                      {{ t('gerichtsterminHelp') }}
                    </span>
                  </button>
                  <button
                    type="button"
                    :class="[
                      'focus:outline-hidden rounded-lg border p-2.5 text-start text-xs',
                      currentTerminType === CalendarEventType.TERMIN
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-card-line hover:bg-card-hover bg-card text-foreground'
                    ]"
                    @click="setTerminType(CalendarEventType.TERMIN)"
                  >
                    <span class="block font-medium">{{ t('allgemeinerTermin') }}</span>
                    <span class="text-muted-foreground-1 mt-0.5 block text-[11px]">
                      {{ t('allgemeinerTerminHelp') }}
                    </span>
                  </button>
                  <button
                    type="button"
                    :class="[
                      'focus:outline-hidden rounded-lg border p-2.5 text-start text-xs',
                      currentTerminType === CalendarEventType.PRIVAT
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-card-line hover:bg-card-hover bg-card text-foreground'
                    ]"
                    @click="setTerminType(CalendarEventType.PRIVAT)"
                  >
                    <span class="block font-medium">{{ t('privat') }}</span>
                    <span class="text-muted-foreground-1 mt-0.5 block text-[11px]">
                      {{ t('privatHelp') }}
                    </span>
                  </button>
                </div>
              </div>

              <div>
                <label class="text-muted-foreground-2 mb-1.5 block text-xs font-medium">
                  {{ t('title') }}
                  <span class="text-rose-500">*</span>
                </label>
                <input
                  v-model="form.title"
                  type="text"
                  :placeholder="t('titlePlaceholder')"
                  :class="[
                    `placeholder:text-muted-foreground-1 focus:outline-hidden block w-full rounded-lg border bg-card px-3
                    py-2 text-sm text-foreground focus:ring-2 focus:ring-primary/30`,
                    titleInvalid ? 'border-rose-300' : 'border-card-line'
                  ]"
                />
              </div>

              <div>
                <label class="text-muted-foreground-2 mb-1.5 block text-xs font-medium">
                  {{ t('fileReference') }}
                  <span class="text-muted-foreground-1 font-normal">- {{ t('optional') }}</span>
                </label>
                <div class="relative">
                  <input
                    v-model="caseQuery"
                    type="text"
                    :placeholder="t('fileReferencePlaceholder')"
                    class="border-card-line placeholder:text-muted-foreground-1 focus:outline-hidden block w-full rounded-lg
                      border bg-card px-3 py-2 pr-9 text-sm text-foreground focus:ring-2 focus:ring-primary/30"
                    @focus="onCaseFocus"
                    @blur="closeCaseSuggestions"
                  />
                  <button
                    v-if="form.case"
                    type="button"
                    class="text-muted-foreground-1 absolute inset-y-0 end-2 inline-flex items-center hover:text-foreground"
                    @click="clearCase"
                  >
                    <svg
                      class="size-3.5 shrink-0"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M18 6 6 18" />
                      <path d="m6 6 12 12" />
                    </svg>
                  </button>
                  <div
                    v-if="showCaseSuggestions && (caseResults.length > 0 || isSearchingCases)"
                    class="border-dropdown-line bg-dropdown absolute z-40 mt-1 max-h-64 w-full overflow-auto rounded-lg border
                      p-1 shadow-lg"
                  >
                    <div
                      v-if="isSearchingCases"
                      class="text-muted-foreground-1 px-2.5 py-2 text-xs"
                    >
                      ...
                    </div>
                    <button
                      v-for="(caseItem, index) in caseResults"
                      :key="caseItem.uuid ?? `case-${index}`"
                      type="button"
                      class="hover:bg-dropdown-item-hover focus:outline-hidden focus:bg-dropdown-item-focus w-full rounded-md
                        px-2.5 py-1.5 text-start text-xs text-foreground"
                      @mousedown.prevent="pickCase(caseItem)"
                    >
                      <span
                        v-if="caseItem.jupus_case_number"
                        class="font-medium"
                      >
                        {{ caseItem.jupus_case_number }}
                      </span>
                      <span class="text-muted-foreground-1">
                        <template v-if="caseItem.jupus_case_number && caseItem.case_title">
                          — {{ caseItem.case_title }}
                        </template>
                        <template v-else>
                          {{ caseItem.case_title }}
                        </template>
                      </span>
                      <span
                        v-if="caseSuggestionMeta(caseItem)"
                        class="text-muted-foreground-1 block truncate text-[11px]"
                      >
                        {{ caseSuggestionMeta(caseItem) }}
                      </span>
                    </button>
                    <div
                      v-if="!isSearchingCases && caseResults.length === 0"
                      class="text-muted-foreground-1 px-2.5 py-2 text-xs"
                    >
                      {{ t('noResults') }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label class="text-muted-foreground-2 mb-1.5 block text-xs font-medium">
                    {{ t('wegen') }}
                  </label>
                  <input
                    v-model="form.wegen"
                    type="text"
                    :placeholder="t('wegenPlaceholder')"
                    class="border-card-line placeholder:text-muted-foreground-1 focus:outline-hidden block w-full rounded-lg
                      border bg-card px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-primary/30"
                  />
                </div>

                <div>
                  <label class="text-muted-foreground-2 mb-1.5 block text-xs font-medium">
                    {{ t('keywords') }}
                  </label>
                  <div
                    class="border-card-line flex min-h-[38px] flex-wrap items-center gap-1.5 rounded-lg border bg-card px-1.5
                      py-1 focus-within:ring-2 focus-within:ring-primary/30"
                  >
                    <span
                      v-for="keyword in form.keywords"
                      :key="keyword"
                      class="bg-surface inline-flex items-center gap-x-1 rounded-md px-1.5 py-0.5 text-[11px] text-foreground"
                    >
                      {{ keyword }}
                      <button
                        type="button"
                        class="text-muted-foreground-1 hover:text-foreground"
                        @click="removeKeyword(keyword)"
                      >
                        <svg
                          class="size-3 shrink-0"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M18 6 6 18" />
                          <path d="m6 6 12 12" />
                        </svg>
                      </button>
                    </span>
                    <input
                      v-model="keywordInput"
                      type="text"
                      class="placeholder:text-muted-foreground-1 focus:outline-hidden min-w-[80px] grow border-0 bg-transparent
                        px-1 py-1 text-xs text-foreground shadow-none focus:ring-0"
                      :placeholder="t('keywordPlaceholder')"
                      @keydown="onKeywordKeydown"
                      @blur="addKeyword"
                    />
                  </div>
                </div>
              </div>

              <div class="space-y-3">
                <div>
                  <label class="text-muted-foreground-2 mb-1.5 block text-xs font-medium">
                    {{ t('start') }}
                    <span class="text-rose-500">*</span>
                  </label>
                  <div
                    :class="['grid gap-2', form.all_day ? 'grid-cols-1' : 'grid-cols-[1fr_auto]']"
                  >
                    <input
                      :value="dateOnly(localStart)"
                      type="date"
                      :min="minimumStartDate"
                      :class="[
                        `focus:outline-hidden block w-full rounded-lg border bg-card px-3 py-2 text-sm text-foreground
                        focus:ring-2 focus:ring-primary/30`,
                        startInvalid ? 'border-rose-300' : 'border-card-line'
                      ]"
                      @change="
                        localStart = composeDateTime(
                          ($event.target as HTMLInputElement).value,
                          timeOnly(localStart)
                        )
                      "
                    />
                    <input
                      v-if="!form.all_day"
                      :value="timeOnly(localStart)"
                      type="time"
                      :min="minimumStartTime"
                      :class="[
                        `focus:outline-hidden w-32 rounded-lg border bg-card px-3 py-2 text-sm text-foreground focus:ring-2
                        focus:ring-primary/30`,
                        startInvalid ? 'border-rose-300' : 'border-card-line'
                      ]"
                      @change="
                        localStart = composeDateTime(
                          dateOnly(localStart),
                          ($event.target as HTMLInputElement).value
                        )
                      "
                    />
                  </div>
                </div>

                <div>
                  <label class="text-muted-foreground-2 mb-1.5 block text-xs font-medium">
                    {{ t('end') }}
                    <span class="text-rose-500">*</span>
                  </label>
                  <div
                    :class="['grid gap-2', form.all_day ? 'grid-cols-1' : 'grid-cols-[1fr_auto]']"
                  >
                    <input
                      :value="dateOnly(localEnd)"
                      type="date"
                      :min="minimumEndDate"
                      :class="[
                        `focus:outline-hidden block w-full rounded-lg border bg-card px-3 py-2 text-sm text-foreground
                        focus:ring-2 focus:ring-primary/30`,
                        endInvalid ? 'border-rose-300' : 'border-card-line'
                      ]"
                      @change="
                        localEnd = composeDateTime(
                          ($event.target as HTMLInputElement).value,
                          timeOnly(localEnd)
                        )
                      "
                    />
                    <input
                      v-if="!form.all_day"
                      :value="timeOnly(localEnd)"
                      type="time"
                      :min="minimumEndTime"
                      :class="[
                        `focus:outline-hidden w-32 rounded-lg border bg-card px-3 py-2 text-sm text-foreground focus:ring-2
                        focus:ring-primary/30`,
                        endInvalid ? 'border-rose-300' : 'border-card-line'
                      ]"
                      @change="
                        localEnd = composeDateTime(
                          dateOnly(localEnd),
                          ($event.target as HTMLInputElement).value
                        )
                      "
                    />
                  </div>
                </div>
              </div>

              <label class="inline-flex items-center gap-x-2 text-xs text-foreground">
                <input
                  v-model="form.all_day"
                  type="checkbox"
                  class="border-line-2 rounded text-primary focus:ring-primary"
                />
                {{ t('allDay') }}
              </label>

              <div>
                <label class="text-muted-foreground-2 mb-1.5 block text-xs font-medium">
                  {{ t('location') }}
                </label>
                <input
                  v-model="form.location_text"
                  type="text"
                  :placeholder="
                    currentTerminType === CalendarEventType.GERICHTSTERMIN
                      ? t('courtLocationPlaceholder')
                      : t('defaultLocationPlaceholder')
                  "
                  class="border-card-line placeholder:text-muted-foreground-1 focus:outline-hidden block w-full rounded-lg
                    border bg-card px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-primary/30"
                />
              </div>

              <div>
                <label class="text-muted-foreground-2 mb-1.5 block text-xs font-medium">
                  {{ t('notes') }}
                </label>
                <textarea
                  v-model="form.description"
                  rows="3"
                  :placeholder="t('notesPlaceholder')"
                  class="border-card-line placeholder:text-muted-foreground-1 focus:outline-hidden block w-full rounded-lg
                    border bg-card px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-primary/30"
                ></textarea>
              </div>
            </section>

            <aside class="flex flex-col gap-y-4 lg:col-span-4">
              <div>
                <label class="text-muted-foreground-2 mb-1.5 block text-xs font-medium">
                  {{ t('calendar') }}
                  <span class="text-rose-500">*</span>
                </label>
                <select
                  v-model="form.calendar"
                  :class="[
                    `focus:outline-hidden block w-full rounded-lg border bg-card px-3 py-2 text-sm text-foreground
                    focus:ring-2 focus:ring-primary/30`,
                    calendarInvalid ? 'border-rose-300' : 'border-card-line'
                  ]"
                >
                  <option
                    v-for="calendar in editableCalendars"
                    :key="calendar.uuid"
                    :value="calendar.uuid"
                  >
                    {{ calendarDisplayLabel(calendar) }}
                  </option>
                </select>
              </div>

              <div>
                <label class="text-muted-foreground-2 mb-1.5 block text-xs font-medium">
                  {{ t('owner') }}
                </label>
                <CalendarPeoplePicker
                  :users="users"
                  :loading="isLoadingUsers"
                  :selected-user-uuids="form.owner ? [form.owner] : []"
                  :multiple="false"
                  :placeholder="t('userPlaceholder')"
                  @update:query="onPeopleQuery"
                  @update:selected-user-uuids="form.owner = $event[0] ?? ''"
                />
              </div>

              <div>
                <label class="text-muted-foreground-2 mb-1.5 block text-xs font-medium">
                  {{ t('assistants') }}
                </label>
                <CalendarPeoplePicker
                  :users="users"
                  :loading="isLoadingUsers"
                  :selected-user-uuids="form.assistants ?? []"
                  :placeholder="t('userPlaceholder')"
                  @update:query="onPeopleQuery"
                  @update:selected-user-uuids="form.assistants = $event"
                />
              </div>

              <div>
                <label class="text-muted-foreground-2 mb-1.5 block text-xs font-medium">
                  {{ t('participants') }}
                </label>
                <p
                  v-if="participantsDisabled"
                  class="text-muted-foreground-1 mb-2 text-[11px]"
                >
                  {{ t('participantsPrivatNote') }}
                </p>
                <p
                  v-else-if="showParticipantsInviteNote"
                  class="text-muted-foreground-1 mb-2 text-[11px]"
                >
                  {{ t('participantsInviteNote') }}
                </p>
                <CalendarPeoplePicker
                  :users="users"
                  :loading="isLoadingUsers"
                  :selected-user-uuids="form.participants_internal ?? []"
                  :selected-emails="form.participantEmails ?? []"
                  allow-emails
                  :disabled="participantsDisabled"
                  :placeholder="t('participantsPlaceholder')"
                  :add-email-label="t('addExternalEmail')"
                  @update:query="onPeopleQuery"
                  @update:selected-user-uuids="form.participants_internal = $event"
                  @update:selected-emails="form.participantEmails = $event"
                />
              </div>

              <div
                v-if="selectedCaseLabel"
                class="border-line-2 bg-surface rounded-lg border p-3 text-[11px]"
              >
                <p class="text-muted-foreground-1 mb-1.5">{{ t('linkedCase') }}</p>
                <p class="font-medium text-foreground">{{ selectedCaseLabel }}</p>
                <p
                  v-if="selectedCase && caseSuggestionMeta(selectedCase)"
                  class="text-muted-foreground-1 mt-0.5"
                >
                  {{ caseSuggestionMeta(selectedCase) }}
                </p>
              </div>
            </aside>
          </div>
        </div>

        <footer
          class="border-card-line flex items-center justify-between gap-2 border-t bg-card px-5 py-3"
        >
          <p
            v-if="!canSave && isTerminMode"
            class="text-muted-foreground-1 text-xs"
          >
            {{ validationMessage }}
          </p>
          <div v-else></div>

          <button
            v-if="isTerminMode"
            type="button"
            :disabled="!canSave || saving"
            :class="[
              'focus:outline-hidden inline-flex items-center gap-x-2 rounded-lg px-3 py-2 text-sm font-medium',
              canSave && !saving
                ? 'focus:bg-primary-focus bg-primary text-primary-foreground hover:bg-primary-hover'
                : 'cursor-not-allowed bg-primary/50 text-primary-foreground'
            ]"
            @click="submit"
          >
            {{ saving ? '...' : saveLabel }}
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>
